import { rootApi } from './baseApi';
import type { User, CreateUserRequest, UpdateUserRequest, BiometricsCredential } from '../types/user';

const transformRolesToType = (roles?: string | number | number[]): 'admin' | 'viewer' => {
  if (roles == null) return 'viewer';

  // Single number (updateUser returns role_id as integer)
  if (typeof roles === 'number') return roles === 1 ? 'admin' : 'viewer';

  // String (e.g. "1,2" from getUserList)
  if (typeof roles === 'string') {
    if (!roles) return 'viewer';
    return roles.split(',').includes('1') ? 'admin' : 'viewer';
  }

  // Number array
  return (roles as number[]).includes(1) ? 'admin' : 'viewer';
};

const transformResponse = (user: any): User => ({
  id: user.id,
  email: user.email,
  firstName: user.firstName,
  lastName: user.lastName,
  phoneNumber: user.phoneNumber,
  isDisabled: user.isDisabled,
  isLockedOut: user.isLockedOut,
  role: transformRolesToType(user.roles),
  created_at: user.created_at,
  updated_at: user.updated_at,
  roles: typeof user.roles === 'number' ? [user.roles] : typeof user.roles === 'string' ? user.roles.split(',').map(Number) : user.roles,
  hasBiometrics: Boolean(user.hasBiometrics)
});

export const userApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    getUserById: builder.query<User, number>({
      query: (userId) => ({
        url: `/users/${userId}`,
        method: 'GET',
      }),
      transformResponse: transformResponse,
    }),

    getUsers: builder.query<User[], void>({
      query: () => ({
        url: '/users',
        method: 'GET',
      }),
      transformResponse: (response: any[]) => 
        response.map(transformResponse),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Users' as const, id })),
              { type: 'Users', id: 'LIST' },
            ]
          : [{ type: 'Users', id: 'LIST' }],
    }),

    createUser: builder.mutation<User, CreateUserRequest>({
      query: (userData) => ({
        url: '/users',
        method: 'POST', 
        body: {
          email: userData.email,
          password: userData.password,
          firstName: userData.firstName,
          lastName: userData.lastName,
          phoneNumber: userData.phoneNumber,
          isDisabled: userData.isDisabled,
          isLockedOut: userData.isLockedOut,
          roles: userData.roles || [2]
        },
      }),
      transformResponse: transformResponse,
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data: newUser } = await queryFulfilled;
          dispatch(
            userApi.util.updateQueryData('getUsers', undefined, (draft) => {
              if (!draft.some((u) => u.id === newUser.id)) {
                draft.push(newUser);
              }
            }),
          );
        } catch {
          /* mutation failed */
        }
      },
    }),

    updateUser: builder.mutation<User, UpdateUserRequest>({
      query: ({ userId, ...update }) => ({
        url: `/users/${userId}`,
        method: 'PATCH',
        body: update,
      }),
      transformResponse: transformResponse,
      async onQueryStarted({ userId }, { dispatch, queryFulfilled }) {
        try {
          const { data: updated } = await queryFulfilled;
          dispatch(
            userApi.util.updateQueryData('getUsers', undefined, (draft) => {
              const idx = draft.findIndex((u) => u.id === userId);
              if (idx !== -1) draft[idx] = updated;
            }),
          );
        } catch {
          /* mutation failed */
        }
      },
    }),

    deleteUser: builder.mutation<void, number>({
      query: (userId) => ({
        url: `/users/${userId}`,
        method: 'DELETE',
      }),
      async onQueryStarted(userId, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          userApi.util.updateQueryData('getUsers', undefined, (draft) => {
            const idx = draft.findIndex((u) => u.id === userId);
            if (idx !== -1) draft.splice(idx, 1);
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),

    checkBiometricsStatus: builder.query<{ hasBiometrics: boolean }, { userId: number }>({
      query: ({ userId }) => ({
        url: `/users/${userId}/biometrics`,
        method: 'GET',
      }),
      transformResponse: (response: any) => ({
        hasBiometrics: Boolean(response.hasBiometrics)
      }),
    }),
  }),
});

export const {
  useGetUsersQuery,
  useGetUserByIdQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useCheckBiometricsStatusQuery
} = userApi;
